import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Wrapper from '../../components/Wrapper'
import { telecomunication } from '../../translate'
import '../../assets/style/style.css'
import Paper from '@mui/material/Paper'
import {
  Box,
  Typography,
  IconButton,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
} from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import Api from '../../Api'
import { telegramConnectStyles } from '../../helpers/style'
import { unreadChannelsSet } from '../../store/actions/users'

export default function WhatsAppConnectNumbers() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const telegramEnabled = useSelector((state) => !!state?.users?.unreadTelegramEnabled)
  const telegramMainId = useSelector((state) => state?.users?.unreadTelegramMainId)
  const langIndex = +localStorage.getItem('atsLang') || 1

  const [numbers, setNumbers] = useState([])
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState('')
  const fetchAttemptedRef = useRef(false)

  const [editItem, setEditItem] = useState(null)
  const [shortGroups, setShortGroups] = useState([])
  const [loadingShortGroups, setLoadingShortGroups] = useState(false)
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)

  useEffect(() => {
    if (fetchAttemptedRef.current) return
    fetchAttemptedRef.current = true
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        setError('')
        const res = await Api.whatsappGet()
        if (cancelled) return
        const data = res.data?.data
        setNumbers(Array.isArray(data) ? data : [])
      } catch (err) {
        if (cancelled) return
        console.error('Failed to get WhatsApp numbers:', err)
        setNumbers([])
        setError(err?.response?.data?.error || err?.message || 'Failed to load numbers')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const startConnect = useCallback(() => {
    setConnecting(true)
    setError('')
    try {
      window.location.href = '/rest/comunication/whatsapp/oauth/start'
    } catch (e) {
      setConnecting(false)
      setError(String(e?.message || e))
    }
  }, [])

  const handleDelete = useCallback(async (id) => {
    const msg = telecomunication.whatsapp_delete_confirm?.[langIndex] || 'Are you sure you want to remove this WhatsApp number?'
    if (!window.confirm(msg)) return
    try {
      await Api.whatsappDelete(id)
      setNumbers((prev) => {
        const next = prev.filter((n) => n.id !== id)
        dispatch(unreadChannelsSet({
          whatsappEnabled: next.length > 0,
          telegramEnabled,
          telegramMainId: telegramMainId != null ? Number(telegramMainId) : null,
        }))
        return next
      })
    } catch (e) {
      console.error('Failed to delete WhatsApp number:', e)
      setError(e?.response?.data?.error || e?.message || 'Failed to delete')
    }
  }, [langIndex, dispatch, telegramEnabled, telegramMainId])

  const openEditDialog = useCallback(async (item) => {
    setEditItem(item)
    setSelectedGroupId('')
    setShortGroups([])
    setLoadingShortGroups(true)
    setError('')
    try {
      const res = await Api.groupsGet(1)
      const raw = res?.data?.data || []
      const shorts = raw
        .filter((g) => +g.isivr === 0 && +g.virtual !== 59999 && +g.virtual !== 568898)
        .map((g) => ({ id: g.id, virtual: g.virtual, name: g.name || '' }))
      setShortGroups(shorts)
      const match = shorts.find((s) => String(s.virtual) === String(item.sip_username ?? '').trim())
      setSelectedGroupId(match ? String(match.id) : '')
    } catch (e) {
      console.error('Failed to load short numbers:', e)
      setError(e?.response?.data?.error || e?.message || 'Failed to load short numbers')
    } finally {
      setLoadingShortGroups(false)
    }
  }, [])

  const closeEditDialog = useCallback(() => {
    setEditItem(null)
    setShortGroups([])
    setSelectedGroupId('')
    setSavingEdit(false)
  }, [])

  const handleSaveShort = useCallback(async () => {
    if (!editItem || !selectedGroupId) return
    try {
      setSavingEdit(true)
      setError('')
      const res = await Api.whatsappAddGroup({
        id: editItem.id,
        group_id: Number(selectedGroupId),
      })
      const sip = res?.data?.sip_username
      setNumbers((prev) => prev.map((n) => (n.id === editItem.id ? { ...n, sip_username: sip != null ? sip : n.sip_username } : n)))
      closeEditDialog()
    } catch (e) {
      console.error('Failed to save short number:', e)
      setError(e?.response?.data?.error || e?.message || 'Failed to save')
    } finally {
      setSavingEdit(false)
    }
  }, [editItem, selectedGroupId, closeEditDialog])

  const Card = ({ item, onDelete, onEdit }) => (
    <div className="connect-card" style={{ cursor: 'default' }}>
      <div className="connect-card-header">
        <h4 className="connect-card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <WhatsAppIcon sx={{ color: '#25D366', fontSize: 28 }} />
          {item.wa_phone_number || `ID ${item.wa_phone_id}`}
        </h4>
      </div>
      <p className="connect-card-desc">
        {item.wa_phone_id ? `${telecomunication.whatsapp_phone_id?.[langIndex] || 'Phone ID'}: ${item.wa_phone_id}` : ''}
        {item.sip_username ? ` · ${telecomunication.whatsapp_short_route?.[langIndex] || 'Short number'}: ${item.sip_username}` : ''}
        {item.updated_at ? ` · ${telecomunication.whatsapp_updated?.[langIndex] || 'Updated'}: ${new Date(item.updated_at).toLocaleString()}` : ''}
      </p>
      <div className="connect-card-action-wrapper">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => onEdit(item)}
            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            aria-label="Edit short number"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(item.id)}
            sx={{ color: '#ce405f', '&:hover': { color: '#ce405f' } }}
            aria-label="Delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </div>
    </div>
  )

  const Section = ({ title, subtitle, children }) => (
    <div className="connect-section">
      <div className="connect-section-header">
        {title ? <h3 className="connect-section-title">{title}</h3> : null}
        {subtitle ? <div className="connect-section-subtitle">{subtitle}</div> : null}
      </div>
      <Box sx={telegramConnectStyles.gridContainer}>
        {children}
      </Box>
    </div>
  )

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3,borderBottom: '1px solid #e5e8ed', pb: 2.5 }}>
            <IconButton
              size="small"
              sx={telegramConnectStyles.backButton}
              onClick={() => navigate(-1)}
              aria-label="Back"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
              {telecomunication.whatsapp_manage_numbers[langIndex] || 'WhatsApp numbers'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Paper sx={{ ...telegramConnectStyles.formPaper, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              {telecomunication.whatsapp_connect_number[langIndex] || 'Connect number'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {telecomunication.whatsapp_manage_numbers_desc[langIndex] || 'Connect and manage multiple WhatsApp Business numbers.'}
            </Typography>
            <Button
              variant="contained"
              onClick={startConnect}
              disabled={connecting}
              sx={{ ...telegramConnectStyles.primaryButton, maxWidth: 220 }}
            >
              {connecting ? (telecomunication.connecting?.[langIndex] || telecomunication.loading?.[langIndex] || 'Connecting...') : (telecomunication.whatsapp_connect_number[langIndex] || 'Connect number')}
            </Button>
          </Paper>

          <Section title="" subtitle="">
            {loading ? (
              <Box sx={{ p: 3, textAlign: 'center', gridColumn: '1 / -1' }}>
                <Typography color="text.secondary">{telecomunication.loading?.[langIndex] || 'Loading...'}</Typography>
              </Box>
            ) : numbers.length > 0 ? (
              numbers.map((item) => (
                <Card key={item.id} item={item} onDelete={handleDelete} onEdit={openEditDialog} />
              ))
            ) : !loading ? (
              <Box sx={telegramConnectStyles.emptyStateBox}>
                <Typography sx={telegramConnectStyles.emptyStateText}>
                  {telecomunication.whatsapp_no_numbers[langIndex] || 'No WhatsApp numbers yet. Connect one to get started.'}
                </Typography>
              </Box>
            ) : null}
          </Section>
        </Paper>
      </div>

      <Dialog open={!!editItem} onClose={closeEditDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {telecomunication.whatsapp_edit_short?.[langIndex] || 'Link short number'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {telecomunication.whatsapp_edit_short_desc?.[langIndex] || 'Choose which internal short number routes this WhatsApp line.'}
          </Typography>
          {editItem ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {editItem.wa_phone_number || `ID ${editItem.wa_phone_id}`}
            </Typography>
          ) : null}
          {loadingShortGroups ? (
            <Typography color="text.secondary">{telecomunication.loading?.[langIndex] || 'Loading...'}</Typography>
          ) : shortGroups.length === 0 ? (
            <Typography color="text.secondary">—</Typography>
          ) : (
            <Autocomplete
              fullWidth
              sx={{ mt: 1 }}
              options={shortGroups}
              getOptionLabel={(g) => `${g.virtual}${g.name ? ` — ${g.name}` : ''}`}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              value={shortGroups.find((g) => String(g.id) === selectedGroupId) || null}
              onChange={(_, newVal) => setSelectedGroupId(newVal ? String(newVal.id) : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={telecomunication.whatsapp_short_route?.[langIndex] || 'Short number'}
                />
              )}
              noOptionsText={langIndex === 2 ? 'Нет совпадений' : langIndex === 3 ? 'Համապատասխանություն չկա' : 'No matches'}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEditDialog} disabled={savingEdit}>
            {telecomunication.modal_button_cancel?.[langIndex] || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveShort}
            disabled={savingEdit || !selectedGroupId || loadingShortGroups}
            sx={telegramConnectStyles.primaryButton}
          >
            {telecomunication.telegram_save_button?.[langIndex] || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  )
}
